import { cn, Checkbox as V2Checkbox } from '@foresightdata/web-common-components';
import { Checkbox as MuiCheckbox } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactElement } from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

type CheckboxVariant = 'secondary' | 'critical';

/**
 * The app's Checkbox: a compact (15px) MUI checkbox today; web-common's Checkbox (Base UI) with
 * the `useV2Components` flag on (FD-6112). `handleChange` is the original callback; `onChange`
 * gets the new checked state, for the MUI call sites that move here. No event, no `sx`.
 *
 * `readOnly`: MUI only marks the input, so the callbacks still fire (the DropdownMenu rows pass
 * it with no callback and handle the click themselves); Base UI blocks the change, so with the
 * flag on they do not. Both match what each library does with the prop.
 */
export interface CheckboxProps {
  /** Omitted when a FormControlLabel supplies it. */
  checked?: boolean;
  handleChange?: () => void;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  readOnly?: boolean;
  variant?: CheckboxVariant;
  name?: string;
  'aria-label'?: string;
  className?: string;
}

export function Checkbox({
  checked,
  handleChange,
  onChange,
  disabled = false,
  indeterminate = false,
  readOnly = false,
  variant = 'secondary',
  name,
  'aria-label': ariaLabel,
  className,
}: CheckboxProps): ReactElement {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();
  // Map variant to MUI color
  const color = variant === 'critical' ? 'error' : 'primary';

  const change = (next: boolean) => {
    handleChange?.();
    onChange?.(next);
  };

  if (!useV2Components) {
    return (
      <MuiCheckbox
        checked={checked}
        onChange={(_event, next) => change(next)}
        disabled={disabled}
        indeterminate={indeterminate}
        inputProps={{ readOnly, 'aria-label': ariaLabel }}
        name={name}
        color={color}
        size='small'
        className={className}
        sx={{
          padding: 0,
          width: 15,
          height: 15,
          '&.MuiCheckbox-root': {
            padding: 0,
          },
          '& .MuiSvgIcon-root': {
            fontSize: 18,
          },
        }}
        data-testid='checkbox'
      />
    );
  }

  return (
    <V2Checkbox
      checked={checked}
      onCheckedChange={(next) => change(next)}
      disabled={disabled}
      indeterminate={indeterminate}
      readOnly={readOnly}
      name={name}
      aria-label={ariaLabel}
      // The app's compact box, the same 15px literal as the MUI branch's `sx` (no spacing token is
      // 15px); `critical` is the destructive colour.
      className={cn(
        'size-[15px]',
        variant === 'critical' && 'data-[checked]:border-destructive data-[checked]:bg-destructive',
        className
      )}
      data-testid='checkbox'
    />
  );
}
